import type { DeepFile } from "@/stores/ai-explorer-store"

/**
 * Categorization for the Deep Explorer "Files" sidebar section.
 *
 * The deepagents library auto-evicts large `ToolMessage` results (typically
 * web search / scrape output) to a virtual path like
 * `/large_tool_results/<tool_call_id>` so the LLM can re-read them via
 * `read_file` without paying the context window. Those files are real and
 * useful to the agent but are internal plumbing from the user's perspective
 * — their `call_abc123` names carry no meaning.
 *
 * We split user-facing artifacts (plan, deliverable, verifications,
 * scratchpad) from those retrieved sources so the UI can present them
 * distinctly and hide the internal dumps by default.
 */
export type FileCategory = "artifact" | "source" | "other"

export type ArtifactKind = "plan" | "deliverable" | "verifications" | "scratchpad" | "generic"

export type SourceKind = "web" | "scrape" | "generic"

export interface CategorizedArtifact {
  file: DeepFile
  category: "artifact"
  displayName: string
  kind: ArtifactKind
}

export interface CategorizedSource {
  file: DeepFile
  category: "source"
  displayName: string
  kind: SourceKind
}

export interface CategorizedOther {
  file: DeepFile
  category: "other"
  displayName: string
}

export type CategorizedFile = CategorizedArtifact | CategorizedSource | CategorizedOther

export interface FilesCategorization {
  artifacts: CategorizedArtifact[]
  sources: CategorizedSource[]
  other: CategorizedOther[]
  /** Map from file path to its categorized entry for O(1) lookups. */
  byPath: Map<string, CategorizedFile>
}

const SOURCES_PREFIX = "/large_tool_results/"

const ARTIFACT_PATHS: Partial<Record<string, { kind: ArtifactKind; displayName: string }>> = {
  "/plan.md": { kind: "plan", displayName: "Plan" },
  "/deliverable.md": { kind: "deliverable", displayName: "Deliverable" },
  "/verifications.json": { kind: "verifications", displayName: "Verifications" },
  "/scratchpad.md": { kind: "scratchpad", displayName: "Scratchpad" },
}

const ARCHIVED_PLAN_RE = /^\/plan-(\d+)\.md$/
const ARCHIVED_DELIVERABLE_RE = /^\/deliverable-(\d+)\.md$/

function matchArchivedArtifact(path: string): { kind: ArtifactKind; displayName: string } | null {
  const planMatch = ARCHIVED_PLAN_RE.exec(path)
  if (planMatch) {
    return { kind: "plan", displayName: `Plan (turn ${planMatch[1]})` }
  }
  const deliverableMatch = ARCHIVED_DELIVERABLE_RE.exec(path)
  if (deliverableMatch) {
    return { kind: "deliverable", displayName: `Deliverable (turn ${deliverableMatch[1]})` }
  }
  return null
}

function isRootLevelMarkdownOrJson(path: string): boolean {
  if (path.startsWith(SOURCES_PREFIX)) return false
  const trimmed = path.startsWith("/") ? path.slice(1) : path
  if (trimmed.includes("/")) return false
  return trimmed.endsWith(".md") || trimmed.endsWith(".json")
}

const MAX_LABEL_LENGTH = 50

function truncate(text: string): string {
  return text.length > MAX_LABEL_LENGTH ? `${text.slice(0, MAX_LABEL_LENGTH)}\u2026` : text
}

function pickStringField(payload: unknown, key: string): string | null {
  if (!payload || typeof payload !== "object") return null
  const value = (payload as Record<string, unknown>)[key]
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname || null
  } catch {
    return null
  }
}

/**
 * Derive a human-readable label for a tool-result source file. Source files
 * under /large_tool_results/ typically contain JSON payloads from web search
 * / scrape / library lookup tools. The activity log formats the same data as
 * `query: "\u2026"` or `title \u2014 url`; mirror that so the Files-dropdown picker
 * doesn't show raw JSON (bug 3 in bugerport_deepagents.pdf).
 *
 * Fallback order: query \u2192 title \u2192 sourcefile \u2192 url (hostname) \u2192 first
 * non-empty line truncated \u2192 "Retrieved result N".
 */
export function deriveSourceDisplayName(file: DeepFile, index: number): string {
  const firstLine = file.content
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.length > 0)

  if (firstLine) {
    let parsed: unknown = null
    try {
      parsed = JSON.parse(firstLine)
    } catch {
      // Not JSON \u2014 fall through to raw-line fallback below.
    }

    if (parsed && typeof parsed === "object") {
      const query = pickStringField(parsed, "query")
      if (query) return truncate(`"${query}"`)
      const title = pickStringField(parsed, "title")
      if (title) return truncate(title)
      const sourcefile = pickStringField(parsed, "sourcefile")
      if (sourcefile) return truncate(sourcefile)
      const url = pickStringField(parsed, "url")
      if (url) {
        const host = hostnameOf(url)
        if (host) return truncate(host)
        return truncate(url)
      }
    }

    return firstLine.length > 40 ? `${firstLine.slice(0, 40)}\u2026` : firstLine
  }

  return `Retrieved result ${index + 1}`
}

function labelForSource(file: DeepFile, index: number): string {
  return deriveSourceDisplayName(file, index)
}

function sourceKind(): SourceKind {
  // Without a reliable way to map a tool_call_id back to its originating
  // tool from the frontend (DeepActivityEvent has no tool_call_id today),
  // default all evicted results to "generic" for now. A future change could
  // enrich the activity log and map by tool name (web_search, web_scrape, …).
  return "generic"
}

function basename(path: string): string {
  return path.split("/").pop() ?? path
}

/**
 * Split `files` into artifacts (user-facing), sources (library-evicted tool
 * results), and other. Artifacts keep their natural order from the input;
 * sources are ordered by `lastModified` ascending so their fallback labels
 * ("Retrieved result N") are stable across re-renders.
 */
export function categorizeFiles(files: DeepFile[]): FilesCategorization {
  const sourceFiles = files
    .filter((f) => f.path.startsWith(SOURCES_PREFIX))
    .slice()
    .sort((a, b) => a.lastModified - b.lastModified)

  const sourceIndexByPath = new Map<string, number>()
  sourceFiles.forEach((f, i) => sourceIndexByPath.set(f.path, i))

  const artifacts: CategorizedArtifact[] = []
  const sources: CategorizedSource[] = []
  const other: CategorizedOther[] = []
  const byPath = new Map<string, CategorizedFile>()

  for (const file of files) {
    const known = ARTIFACT_PATHS[file.path] ?? matchArchivedArtifact(file.path)
    if (known) {
      const entry: CategorizedArtifact = {
        file,
        category: "artifact",
        displayName: known.displayName,
        kind: known.kind,
      }
      artifacts.push(entry)
      byPath.set(file.path, entry)
      continue
    }
    if (file.path.startsWith(SOURCES_PREFIX)) {
      const index = sourceIndexByPath.get(file.path) ?? 0
      const entry: CategorizedSource = {
        file,
        category: "source",
        displayName: labelForSource(file, index),
        kind: sourceKind(),
      }
      sources.push(entry)
      byPath.set(file.path, entry)
      continue
    }
    if (isRootLevelMarkdownOrJson(file.path)) {
      const entry: CategorizedArtifact = {
        file,
        category: "artifact",
        displayName: basename(file.path),
        kind: "generic",
      }
      artifacts.push(entry)
      byPath.set(file.path, entry)
      continue
    }
    const entry: CategorizedOther = {
      file,
      category: "other",
      displayName: basename(file.path),
    }
    other.push(entry)
    byPath.set(file.path, entry)
  }

  return { artifacts, sources, other, byPath }
}

/** True if a file path is an internal eviction dump, not a user artifact. */
export function isInternalToolResult(path: string): boolean {
  return path.startsWith(SOURCES_PREFIX)
}
